import { Component, OnInit } from '@angular/core';
import { AppContextService } from '../../../../../services/app-context.service';
import { LogService } from '../../../../../services/log/log.service';
import { OperationError } from '../../../../../models/server-result/operation-error';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { UtilitiesService } from '../../../../../services/utils/utilities.service';
import { User } from '../../../../../models/User/user';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { UploadSuppliersInstructionsModalComponent } from '../instructions-modal/upload-suppliers-instructions-modal.component';
import { Router, ActivatedRoute } from '@angular/router';
import { Company } from '../../../../../models/companies/company';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { SelectOption } from '../../../../../shared-components/custom-select/custom-select.component';
import { ContractTabService } from '../../../../../services/contract-tab.service';
import { CustomModalService } from '../../../../../shared-components/custom-modal/custom-modal.service';
import { CompanyApiService } from '../../../../../server-api/company-api/company-api.service';

@Component({
	selector: 'app-suppliers-upload-step1',
	templateUrl: './suppliers-upload-step1.component.html',
	styleUrls: ['./suppliers-upload-step1.component.css'],
	standalone: false,
})
export class SuppliersUploadStep1Component implements OnInit {
	public isPerformingRequest = false;
	public validationErrors: OperationError[] = [];
	public loggedUser: User | null = null;
	public requestErrors: OperationError[] = [];
	protected companies: Company[] = [];
	protected associatedCompanyId: string = '';
	protected initialCompanyId: string = '';
	protected lockSelections: boolean = false;
	private returnContractId: string = '';
	protected financierCompanyIdRequiredError: string | null = null;
	protected _companyOptions: SelectOption[] = [];

	currentStep: number = 0;
	steps: Step[] = [];

	constructor(
		private companyApiService: CompanyApiService,
		private appContext: AppContextService,
		private logger: LogService,
		private toasterService: ToasterService,
		private utilitiesService: UtilitiesService,
		private translateService: TranslateService,
		private modalService: CustomModalService,
		private router: Router,
		private route: ActivatedRoute,
		private routeAuthorizationService: RouteAuthorizationService,
		private contractTabService: ContractTabService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		const queryParams = this.route.snapshot.queryParams;
		if (queryParams['buyerId']) {
			this.initialCompanyId = queryParams['buyerId'];
			this.lockSelections = true;
		}
		if (queryParams['returnContractId']) {
			this.returnContractId = queryParams['returnContractId'];
		}

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (
				(this.loggedUser?.IsBuyer && this.loggedUser?.IsCompanyAdmin) ||
				(this.loggedUser?.IsSystemAdmin && !this.loggedUser.IsApiIntegrationUser) ||
				this.loggedUser?.IsSupportOperator
			) {
				this.loadData();
				setTimeout(() => this.openInstructionsModal(), 0);
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	private loadData(): void {
		if (!this.loggedUser?.Companies && !this.loggedUser?.IsSystemAdmin) {
			this.toasterService.showError(
				this.translateService.translate('suppliers-upload-step1.could-not-get-logged-user-associated-companies', 'client')
			);
		} else {
			this.isPerformingRequest = true;
			this.companyApiService.getAllConfirmingAccessibleBuyersForUser().subscribe({
				next: (operationResult: OperationResult<any[]>) => {
					this.isPerformingRequest = false;
					if (operationResult.IsValid && !!operationResult.Data) {
						this.companies = operationResult.Data;
						this.onCompaniesLoaded();
					} else {
						this.processInvalidResult(operationResult);
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					this.isPerformingRequest = false;
					this.logger.error('[SuppliersUploadStep1Component::loadData] HttpErrorResponse: ', httpErrorResponse);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				},
			});
		}
	}

	private onCompaniesLoaded(): void {
		if (this.lockSelections && this.initialCompanyId) {
			const selectedCompany = this.companies.find((c) => c.Id === this.initialCompanyId);
			if (selectedCompany) {
				this.companies = [selectedCompany];
			}
		}

		this.updateCompanyOptions();

		if (this.initialCompanyId) {
			this.associatedCompanyId = this.initialCompanyId;
		} else if (this.companies?.length === 1) {
			this.associatedCompanyId = this.companies[0].Id!;
		}
	}

	private processInvalidResult<T>(operationResult: OperationResult<T>) {
		const errors = this.utilitiesService.GetDistinctOperationErrors(operationResult.Errors);

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	private formIsValid(): boolean {
		this.financierCompanyIdRequiredError = !this.associatedCompanyId ? 'REQUIRED' : null;
		return !this.financierCompanyIdRequiredError;
	}

	protected openInstructionsModal(): void {
		const instructionsModalRef = this.modalService.open<string>({
			size: 'lg',
			title: this.translateService.translate('supplier-upload-instructions-modal.title', 'client'),
			body: {
				kind: 'component',
				componentType: UploadSuppliersInstructionsModalComponent,
			},
			actions: [
				{
					id: 'start',
					label: this.translateService.translate('button.start', 'client') || 'Start',
					color: 'primary',
					variant: 'outlined',
					autoFocus: true,
					run: ({ bodyComponent, modalRef }) => {
						const result = bodyComponent?.start();
						modalRef.close(result);
					},
				},
			],
		});

		instructionsModalRef.result.catch(() => undefined);
	}

	protected redirectToUploadSuppliersStep2(): void {
		if (this.formIsValid()) {
			const queryParams = this.route.snapshot.queryParams;
			this.router.navigate(['/private/files-upload/upload/suppliers/step2', this.associatedCompanyId], {
				queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
			});
		}
	}

	protected hasEnteredRequiredData(): boolean {
		return !!this.associatedCompanyId;
	}

	protected onCompanySelectionChange(companyId: string): void {
		this.associatedCompanyId = companyId;
		this.financierCompanyIdRequiredError = null;
	}

	protected get companyOptions(): SelectOption[] {
		return this._companyOptions;
	}

	private updateCompanyOptions(): void {
		this._companyOptions = (this.companies || []).map((company) => ({
			label: `${company.Name} - ${company.CountryCode}${company.VatNumber}`,
			value: company.Id,
		}));
	}

	protected redirectToCompanies(): void {
		if (this.returnContractId) {
			this.contractTabService.setSelectedTab('confirming', 'suppliers');
			this.router.navigate(['/private/contracts/confirming/details', this.returnContractId]);
		} else {
			this.router.navigate(['/private/companies']);
		}
	}
}
